<!-- Search + Filters + View Toggle + secondary actions — single row.
     Layout (left → right):
       [search] [gridFilters slot] [view-toggle] [gridActions slot]
     Page-level create ("Add X") lives on `<app-page-header>` `[actions]`
     (title row, far right). `gridActions` has refresh and other list
     chrome — view-toggle precedes it so secondary actions stay in the
     right cluster without crowding the title. -->
@if (showSearch() && !accessDenial()) {
  <!-- Suppressed wholesale on a denial. Every control in this row acts on a
       list the caller cannot read: search and filters would narrow nothing. -->
  <div class="search-filter-bar">
    <app-toolbar-search
      [value]="searchQuery()"
      [placeholder]="searchPlaceholder()"
      (valueChange)="searchChange.emit($event)"
    />

    <ng-content select="[gridFilters]"></ng-content>

    <div class="search-bar-right">
      <!-- View Selector — precedes gridActions (refresh, etc.). -->
      <div class="view-selector">
        <button
          type="button"
          class="view-button"
          [class.active]="viewMode() === 'grid'"
          (click)="viewMode.set('grid')"
          [attr.aria-label]="'resource_grid.grid_view' | transloco"
          [title]="'resource_grid.grid_view' | transloco"
        >
          <mat-icon>grid_on</mat-icon>
        </button>
        <button
          type="button"
          class="view-button"
          [class.active]="viewMode() === 'list'"
          (click)="viewMode.set('list')"
          [attr.aria-label]="'resource_grid.list_view' | transloco"
          [title]="'resource_grid.list_view' | transloco"
        >
          <mat-icon>list</mat-icon>
        </button>
      </div>

      <ng-content select="[gridActions]"></ng-content>
    </div>
  </div>
}

<!-- Content Area -->
@if (loading()) {
  @if (viewMode() === 'list') {
    @if (listSkeletonTpl(); as tpl) {
      <ng-container [ngTemplateOutlet]="tpl"></ng-container>
    } @else {
      <app-skeleton type="table" [rows]="skeletonCount()" [columns]="5"></app-skeleton>
    }
  } @else {
    @if (gridSkeletonTpl(); as tpl) {
      <ng-container [ngTemplateOutlet]="tpl"></ng-container>
    } @else {
      <div class="resource-grid" [style.--min-card-width]="minCardWidth()">
        @for (item of skeletonItems(); track item) {
          <app-skeleton [height]="'200px'"></app-skeleton>
        }
      </div>
    }
  }
} @else if (accessDenial()) {
  <!-- Checked BEFORE `error()`: routes set both, because the raw string
       still feeds anything that reads it. A denial is an answer, not a
       failure, so it must not reach the error hero + retry below. -->
  <app-access-notice [message]="accessDenial()!"></app-access-notice>
} @else if (error()) {
  <app-error-state
    [message]="error() || ('resource_grid.error_loading' | transloco)"
    (retry)="errorRetry.emit()"
  ></app-error-state>
} @else if (isEmpty()) {
  <ng-content select="[gridEmptyState]"></ng-content>
} @else {
  <!-- Grid View -->
  @if (viewMode() === 'grid') {
    <div class="resource-grid" [style.--min-card-width]="minCardWidth()">
      <ng-content select="[gridView]"></ng-content>
    </div>
  }

  <!-- List View -->
  @if (viewMode() === 'list') {
    <ng-content select="[listView]"></ng-content>
  }

  <!-- Infinite scroll sentinel (always present to avoid layout shift) -->
  <div class="pagination-container" [class.hidden]="!hasMore() && !loadingMore()">
    <div #scrollSentinel class="scroll-sentinel"></div>
    @if (loadingMore()) {
      <div class="loading-more-indicator">
        <div class="loading-spinner-dots"><span></span><span></span><span></span></div>
        <span class="loading-more-text">{{ 'resource_grid.loading_more' | transloco }}</span>
      </div>
    } @else if (hasMore()) {
      <button class="load-more-button" type="button" (click)="loadMore.emit()">
        {{ 'resource_grid.load_more' | transloco }}
      </button>
    }
  </div>
}
